import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Snowflake, Sun, Wrench, Sprout, Gauge, ShieldCheck, Clock, MapPin, ArrowRight, ThermometerSnowflake, ThermometerSun } from 'lucide-react';
import { SiteNav } from '../components/SiteNav';
import { SiteFooter } from '../components/SiteFooter';
import { EstimateForm } from '../components/EstimateForm';
import { Button } from '../components/ui/button';
import { getSeason, SEASONS, serviceCard } from '../lib/season';

const ICONS = { Snowflake, Sun, Wrench, Sprout, Gauge };

const ServiceCard = ({ s, i }) => {
  const Icon = ICONS[s.icon];
  return (
    <article className="w-card" style={{ animationDelay: `${i * 120}ms` }} data-testid={`service-card-${s.id}`}>
      <div className="w-card-img" style={{ backgroundImage: `url(${s.img})` }}><Icon size={22} /></div>
      <div className="w-card-body">
        <h3>{s.title}</h3>
        <p>{s.text}</p>
        <ul>{s.points.map((p) => <li key={p}>{p}</li>)}</ul>
      </div>
    </article>
  );
};

const Snow = () => (
  <div className="w-snow" aria-hidden="true">
    {Array.from({ length: 28 }).map((_, i) => (
      <span key={i} style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i % 7) * 1.3}s`, animationDuration: `${8 + (i % 5) * 2}s`, opacity: 0.35 + (i % 4) * 0.15 }} />
    ))}
  </div>
);

const TrustAndTeaser = () => (
  <>
    <ul className="w-trust">
      <li><ShieldCheck size={18} /> Licensed &amp; insured</li>
      <li><Clock size={18} /> Call back within 1 business day</li>
      <li><MapPin size={18} /> Waxahachie &amp; Greater DFW</li>
    </ul>
    <div className="w-backflow-teaser" data-testid="backflow-teaser">
      <h4>Need a backflow test or repair?</h4>
      <p>Book a certified backflow test online in under a minute.</p>
      <Link to="/backflow" data-testid="teaser-backflow-link">Go to Backflow <ArrowRight size={14} /></Link>
    </div>
  </>
);

export default function SprinklerHome() {
  const { hash } = useLocation();
  const season = getSeason();
  const c = SEASONS[season];
  const cards = useMemo(() => c.services.map(serviceCard), [c]);
  const AlertIcon = ICONS[c.alertIcon];
  const EyebrowIcon = season === 'winter' ? ThermometerSnowflake : ThermometerSun;

  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const el = document.getElementById(hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [hash]);
  const goEstimate = () => document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className={`site-theme ${c.theme}`} data-testid="sprinkler-home" data-season={season}>
      <SiteNav />
      <section className="w-hero" style={{ backgroundImage: `url(${c.heroImg})` }} data-testid="sprinkler-hero">
        <div className="w-hero-shade" />
        {c.snow && <Snow />}
        <div className="w-container w-hero-content">
          <span className="w-eyebrow" data-testid="season-eyebrow"><EyebrowIcon size={16} /> {c.eyebrow}</span>
          <h1 data-testid="hero-title">{c.title}</h1>
          <p>{c.lead}</p>
          <div className="w-hero-actions">
            <Button size="lg" className="w-btn-primary" onClick={goEstimate} data-testid="hero-estimate-button">Get a Free Estimate</Button>
            <Link to="/backflow" className="w-btn-ghost" data-testid="hero-backflow-link">Backflow testing &amp; repair <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      <section className="w-alert" id={c.anchor} data-testid="season-alert">
        <div className="w-container w-alert-inner">
          <AlertIcon size={28} />
          <div>
            <h2>{c.alertTitle}</h2>
            <p>{c.alertText}</p>
          </div>
          <Button className="w-btn-primary" onClick={goEstimate} data-testid="alert-estimate-button">{c.alertCta}</Button>
        </div>
      </section>

      <section className="w-section" id="services" data-testid="sprinkler-services">
        <div className="w-container">
          <p className="w-kicker">What we do</p>
          <h2 className="w-title">{c.servicesTitle}</h2>
          <div className="w-cards">{cards.map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}</div>
        </div>
      </section>

      <section className="w-section w-section-frost" id="estimate" data-testid="estimate-section">
        <div className="w-container w-estimate-grid">
          <div className="w-estimate-copy">
            <p className="w-kicker">Free estimate</p>
            <h2 className="w-title">Tell us what you need — we'll call you back.</h2>
            <p>No obligation. Add a few photos and we can often quote faster. We'll explain the options and give you a clear price before any work starts.</p>
            <TrustAndTeaser />
          </div>
          <div className="w-estimate-card"><EstimateForm options={cards} /></div>
        </div>
      </section>

      <a href="tel:4695054321" className="pbnj-sticky-call" data-testid="sticky-call-button">Call/Text 469-505-4321</a>
      <SiteFooter />
    </div>
  );
}
